// src/components/health/timeline/TimelineFilters.tsx
// Filter Controls for Health Timeline

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Filter,
  X,
  Search,
  ChevronDown,
  Check,
  Zap,
  Pill,
  Heart,
  Thermometer,
  Moon,
  Activity,
} from 'lucide-react';
import type { TimelineFilters as TimelineFiltersType } from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Types
// ============================================================================

interface TimelineFiltersProps {
  filters: TimelineFiltersType;
  onChange: (filters: TimelineFiltersType) => void;
  availableTypes: string[];
  availableCategories: string[];
}

// ============================================================================
// Category Config
// ============================================================================

const categoryConfig: Record<string, { icon: React.FC<{ className?: string }>; label: string }> = {
  seizure: { icon: Zap, label: 'Seizures' },
  medication: { icon: Pill, label: 'Medications' },
  therapy: { icon: Heart, label: 'Therapy' },
  symptom: { icon: Thermometer, label: 'Symptoms' },
  sleep: { icon: Moon, label: 'Sleep' },
  vital: { icon: Activity, label: 'Vitals' },
};

// ============================================================================
// Component
// ============================================================================

export const TimelineFilters: React.FC<TimelineFiltersProps> = ({
  filters,
  onChange,
  availableTypes,
  availableCategories,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  const hasActiveFilters = 
    filters.types.length > 0 || 
    filters.categories.length > 0 || 
    filters.searchQuery || 
    filters.severity;

  const activeFilterCount = [
    filters.types.length > 0,
    filters.categories.length > 0,
    !!filters.searchQuery,
    !!filters.severity,
  ].filter(Boolean).length;

  const toggleCategory = (category: string) => {
    const newCategories = filters.categories.includes(category)
      ? filters.categories.filter((c) => c !== category)
      : [...filters.categories, category];
    onChange({ ...filters, categories: newCategories });
  };

  const clearFilters = () => {
    onChange({
      types: [],
      categories: [],
      searchQuery: undefined,
      severity: undefined,
    });
  };

  return (
    <div className="space-y-3">
      {/* Main Filter Bar */}
      <div className="flex items-center gap-3">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
          <input
            type="text"
            value={filters.searchQuery || ''}
            onChange={(e) => onChange({ ...filters, searchQuery: e.target.value || undefined })}
            placeholder="Search timeline..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-cream-dark bg-white
              focus:ring-2 focus:ring-sage/20 focus:border-sage outline-none
              text-sm placeholder:text-warm-gray"
          />
        </div>

        {/* Category Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowCategories(!showCategories)}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-xl border
              text-sm font-medium transition-colors
              ${filters.categories.length > 0
                ? 'bg-sage/10 border-sage/30 text-sage-dark'
                : 'bg-white border-cream-dark text-navy hover:border-sage-light'
              }
            `}
          >
            <Filter className="w-4 h-4" />
            Categories
            {filters.categories.length > 0 && (
              <span className="w-5 h-5 rounded-full bg-sage text-white text-xs flex items-center justify-center">
                {filters.categories.length}
              </span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${showCategories ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {showCategories && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl border border-cream-dark shadow-medium z-20"
              >
                <div className="p-2">
                  {availableCategories.map((category) => {
                    const config = categoryConfig[category] || { icon: Activity, label: category };
                    const IconComponent = config.icon;
                    const isSelected = filters.categories.includes(category);

                    return (
                      <button
                        key={category}
                        onClick={() => toggleCategory(category)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2 rounded-lg
                          text-sm transition-colors
                          ${isSelected
                            ? 'bg-sage/10 text-sage-dark'
                            : 'text-navy hover:bg-cream'
                          }
                        `}
                      >
                        <IconComponent className="w-4 h-4" />
                        <span className="flex-1 text-left">{config.label}</span>
                        {isSelected && <Check className="w-4 h-4" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* More Filters Toggle */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-xl border
            text-sm font-medium transition-colors
            ${hasActiveFilters
              ? 'bg-sage/10 border-sage/30 text-sage-dark'
              : 'bg-white border-cream-dark text-navy hover:border-sage-light'
            }
          `}
        >
          More Filters
          {activeFilterCount > 0 && (
            <span className="w-5 h-5 rounded-full bg-sage text-white text-xs flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Clear Button */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="p-2.5 rounded-xl border border-cream-dark text-warm-gray
              hover:text-navy hover:border-navy/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Expanded Filters */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 rounded-xl bg-cream/50 border border-cream-dark space-y-4">
              {/* Severity Range */}
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Severity Range
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={filters.severity?.min || 1}
                      onChange={(e) => onChange({
                        ...filters,
                        severity: {
                          min: parseInt(e.target.value),
                          max: filters.severity?.max || 10,
                        },
                      })}
                      className="w-full accent-sage"
                    />
                    <div className="flex justify-between text-xs text-warm-gray mt-1">
                      <span>Min: {filters.severity?.min || 1}</span>
                    </div>
                  </div>
                  <span className="text-warm-gray">to</span>
                  <div className="flex-1">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={filters.severity?.max || 10}
                      onChange={(e) => onChange({
                        ...filters,
                        severity: {
                          min: filters.severity?.min || 1,
                          max: parseInt(e.target.value),
                        },
                      })}
                      className="w-full accent-sage"
                    />
                    <div className="flex justify-between text-xs text-warm-gray mt-1">
                      <span>Max: {filters.severity?.max || 10}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Severity Presets */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onChange({ ...filters, severity: { min: 1, max: 4 } })}
                  className={`
                    px-3 py-1.5 rounded-lg text-xs font-medium
                    ${filters.severity?.min === 1 && filters.severity?.max === 4
                      ? 'bg-sage text-white'
                      : 'bg-white border border-cream-dark text-navy hover:border-sage-light'
                    }
                  `}
                >
                  Low (1-4)
                </button>
                <button
                  onClick={() => onChange({ ...filters, severity: { min: 5, max: 7 } })}
                  className={`
                    px-3 py-1.5 rounded-lg text-xs font-medium
                    ${filters.severity?.min === 5 && filters.severity?.max === 7
                      ? 'bg-terracotta text-white'
                      : 'bg-white border border-cream-dark text-navy hover:border-sage-light'
                    }
                  `}
                >
                  Medium (5-7)
                </button>
                <button
                  onClick={() => onChange({ ...filters, severity: { min: 8, max: 10 } })}
                  className={`
                    px-3 py-1.5 rounded-lg text-xs font-medium
                    ${filters.severity?.min === 8 && filters.severity?.max === 10
                      ? 'bg-red-500 text-white'
                      : 'bg-white border border-cream-dark text-navy hover:border-sage-light'
                    }
                  `}
                >
                  High (8-10)
                </button>
                <button
                  onClick={() => onChange({ ...filters, severity: undefined })}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium
                    bg-white border border-cream-dark text-navy hover:border-sage-light"
                >
                  All
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-warm-gray">Active filters:</span>
          
          {filters.categories.map((category) => {
            const config = categoryConfig[category] || { label: category };
            return (
              <button
                key={category}
                onClick={() => toggleCategory(category)}
                className="flex items-center gap-1 px-2 py-1 rounded-full
                  bg-sage/10 text-sage-dark text-xs font-medium
                  hover:bg-sage/20 transition-colors"
              >
                {config.label}
                <X className="w-3 h-3" />
              </button>
            );
          })}

          {filters.searchQuery && (
            <button
              onClick={() => onChange({ ...filters, searchQuery: undefined })}
              className="flex items-center gap-1 px-2 py-1 rounded-full
                bg-navy/10 text-navy text-xs font-medium
                hover:bg-navy/20 transition-colors"
            >
              "{filters.searchQuery}"
              <X className="w-3 h-3" />
            </button>
          )}

          {filters.severity && (
            <button
              onClick={() => onChange({ ...filters, severity: undefined })}
              className="flex items-center gap-1 px-2 py-1 rounded-full
                bg-terracotta/10 text-terracotta text-xs font-medium
                hover:bg-terracotta/20 transition-colors"
            >
              Severity {filters.severity.min}-{filters.severity.max}
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineFilters;

